import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  booleanAttribute,
  Component,
  inject,
  input,
  InputSignal,
  InputSignalWithTransform,
  OnInit,
  output,
} from '@angular/core';
import { ClickOutside } from 'ngxtension/click-outside';
import { ActionSheetService } from '../action-sheet/action-sheet.service';
import { controlValueAccessorProvider } from '../common/value-accessor';
import { Input } from '../input/input.component';
import { Label } from '../label/label.component';
import { SelectOption } from './option/default/select-option.component';
import { SelectAdapter } from './select.adapter';
import { SelectStore } from './select.store';
import { Button } from '../button/button.component';

export interface SelectInput {
  _options: InputSignal<any[]>;

  bindValue?: InputSignal<string | undefined>;
  bindLabel?: InputSignal<string | undefined>;

  /**
   * @description 검색 기능 사용 여부
   */
  search?: InputSignalWithTransform<boolean, string>;

  /**
   * @description custom select option 렌더링 컴포넌트
   */
  renderItem: InputSignal<any | undefined>;

  /**
   * @description 앞 아이콘
   */
  prefixIcon?: InputSignal<string | undefined>;

  /**
   * @description 뒤 아이콘
   */
  suffixIcon?: InputSignal<string | undefined>;
}

@Component({
  host: {
    ngSkipHydration: 'true',
  },
  selector: 'app-select',
  styleUrls: ['../input/input.component.scss', './select.component.scss'],
  standalone: true,
  imports: [CommonModule, Input, Label, SelectOption, ClickOutside, Button],
  templateUrl: './select.component.html',
  providers: [SelectStore, controlValueAccessorProvider(Select)],
})
export class Select
  extends SelectAdapter
  implements OnInit, AfterViewInit, SelectInput
{
  readonly actionSheetService = inject(ActionSheetService);

  /**
   * @description custom option을 사용할 경우 사용하는 output
   */
  customEvent = output<any>();

  renderItem = input<any>();
  prefixIcon = input<string>();
  suffixIcon = input<string>();
  modalTitle = input<string>();
  actionSheetMode = input<boolean, string>(false, {
    transform: booleanAttribute,
  });

  /**
   * @description action sheet 열기(모바일에서만 사용)
   */
  handleActionSheet(ev: Event) {
    ev?.stopPropagation();
    ev?.preventDefault();

    // actionSheetMode가 true일 경우 actionSheet로 열기
    if (this.actionSheetMode()) {
      this.actionSheet();
    } else {
      if (!this.isMobile()) {
        super.handleOpen(ev);
      }
    }
  }

  /**
   * @name actionSheet
   * @description action sheet 열기
   * @returns {void}
   */
  actionSheet(): void {
    const options = this.options().map((option) => {
      const label =
        this.store.getLabel(option, this.bindLabel()!) || option?.label;
      const value =
        this.store.getValue(option, this.bindValue()!) || option?.value;
      return {
        label,
        value,
        handler: () => {
          this.value.set(value);
          this.inputValue.set(label);
        },
      };
    });

    const modal = this.actionSheetService.create({
      options,
      title: this.modalTitle()
        ? this.modalTitle()
        : this.label()
          ? `${this.label()} 선택`
          : '선택',
      renderItem: this.renderItem(),
      currentValue: this.value(),
    });

    modal.closed.subscribe({
      next: (value) => {
        this.value.set(value);
      },
    });
  }

  /**
   * Custom Option을 사용할 경우 custom event를 처리
   */
  handleCustomEvent(ev: any) {
    this.customEvent.emit(ev);
  }
}
